/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Paper color: `light` (organic white sheet) or `dark` (ink sheet). */
export type RipperPaperTone = 'light' | 'dark';

/**
 * Readable text color for text sitting ON a paper sheet of each tone. Its own module rather than
 * `index.tsx` so both RipperPaper and `backdrop.tsx` — which paints the paper texture itself for the
 * Hero and Article Hero instead of using the component — can import it: exporting a const from a
 * component module trips `react-refresh/only-export-components`, and duplicating the map would leave
 * two copies to keep in sync.
 *
 * Note that `light` maps to `--bm-ink`, Content/Primary **text** — NOT the `--bm-black` surface a
 * dark section paints. The two were interchangeable until UCR-3078 gave the dark surface its own
 * value; see `ai-docs/dark-surface/`.
 */
export const TONE_TEXT: Record<RipperPaperTone, string> = {
    light: 'var(--bm-ink)',
    dark: 'var(--bm-organic-white)',
};
